<script setup>
import { useStore } from 'vuex';
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue';
import DropdownComponent from '@/components/DropdownComponent.vue';
const {
  source,
  dropdownList,
  canShowDropdown,
  selectedItemId,
  selectedClass,
  buttonClass,
} = defineProps({
  canShowDropdown: {
    type: Boolean,
    required: false,
    default: true,
  },
  dropdownList: {
    type: Array,
    required: true,
  },
  selectedItemId: {
    type: [String, Number],
    required: false,
    default: null,
  },
  selectedClass: {
    type: String,
    required: false,
    default: 'text-steel-900',
  },
  buttonClass: {
    type: String,
    required: false,
    default:
      'subtle-button rounded-[5px] flex items-center justify-center gap-1',
  },
  source: {
    type: String,
    required: false,
    default: '',
  },
});
const store = useStore();
const emit = defineEmits(['selectItem']);

const showDropdown = ref(false);
const isDropdownVisible = computed(() => showDropdown.value && canShowDropdown);
const showCaret = computed(() => canShowDropdown && dropdownList.length > 0);

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const selectedItem = ref(null);
watch(
  () => dropdownList,
  (newValue) => {
    if (newValue && newValue.length) {
      if (selectedItemId === null) {
        selectedItem.value = newValue[0]; // Select the first item if no ID is provided
      } else {
        selectedItem.value =
          newValue.find((item) => item.id === selectedItemId) || null;
      }
    }
  },
  { immediate: true }
);
const handleDropdownClick = (item, source) => {
  // console.log('Dropdown item clicked:', item, source);
  selectedItem.value = item;
  showDropdown.value = false;
  emit('selectItem', source, item);
};

//click away
const clickOutsideRef = ref(null);
const handleClickOutside = (event) => {
  if (clickOutsideRef.value && !clickOutsideRef.value.contains(event.target)) {
    showDropdown.value = false;
  }
};
const getIsTabLoading = (tab) => {
  return store.state.myLoadsStore[tab].isLoading;
};
const getTabTotalRecords = (tab) => {
  return store.state.myLoadsStore[tab].pagination.totalRecords;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    ref="clickOutsideRef"
    class="relative"
  >
    <button
      :class="[buttonClass]"
      @click="toggleDropdown"
    >
      <div
        class="flex flex-col text-sm font-medium leading-normal tracking-tight"
        :class="selectedClass"
      >
        <div class="flex gap-1">
          <div>{{ selectedItem?.label }}</div>
          <div
            :class="[
              {
                'skeleton-animation ml-1 mt-0.5 h-4 w-4 rounded-full text-transparent':
                  getIsTabLoading(selectedItem?.id),
              },
              getIsTabLoading(selectedItem?.id)
                ? 'bg-sun-400'
                : 'bg-tansparent',
            ]"
          >
            {{
              getIsTabLoading(selectedItem?.id)
                ? ''
                : `(${getTabTotalRecords(selectedItem?.id)})`
            }}
          </div>
        </div>
        <div
          v-if="!getIsTabLoading(selectedItem?.id)"
          class="h-0.5 w-full rounded-sm bg-sun-400"
        ></div>
      </div>
      <div
        v-if="showCaret"
        class="relative h-4 w-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.99997 6.94278C3.89037 6.83318 3.83557 6.77838 3.80266 6.72137C3.71334 6.56667 3.71334 6.37607 3.80266 6.22137C3.83557 6.16437 3.89037 6.10957 3.99997 5.99997C4.10957 5.89037 4.16437 5.83557 4.22137 5.80266C4.37607 5.71334 4.56667 5.71334 4.72137 5.80266C4.77838 5.83557 4.83318 5.89037 4.94278 5.99997L7.43428 8.49147C7.63229 8.68948 7.7313 8.78849 7.84546 8.82558C7.94588 8.85821 8.05406 8.85821 8.15448 8.82558C8.26864 8.78849 8.36765 8.68948 8.56565 8.49147L11.0572 5.99997C11.1668 5.89037 11.2216 5.83557 11.2786 5.80266C11.4333 5.71334 11.6239 5.71334 11.7786 5.80266C11.8356 5.83557 11.8904 5.89037 12 5.99997C12.1096 6.10957 12.1644 6.16437 12.1973 6.22137C12.2866 6.37607 12.2866 6.56667 12.1973 6.72137C12.1644 6.77838 12.1096 6.83318 12 6.94278L8.56565 10.3771C8.36765 10.5751 8.26864 10.6741 8.15448 10.7112C8.05406 10.7438 7.94588 10.7438 7.84546 10.7112C7.7313 10.6741 7.63229 10.5751 7.43428 10.3771L3.99997 6.94278Z"
            fill="#6C7C9D"
          />
        </svg>
      </div>
    </button>
    <DropdownComponent
      :show-count="true"
      :get-count="getTabTotalRecords"
      :is-dropdown-visible="isDropdownVisible"
      :selected-item-id="selectedItem.id"
      :selected-class="'border-sun-400 text-sun-400'"
      text-size="text-sm"
      :source="source"
      :dropdown-list="dropdownList"
      @dropdown-click="handleDropdownClick"
    />
  </div>
</template>

<style scoped>
.default-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-900);
}
.subtle-button {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
  color: var(--color-steel-900);
}
</style>
